import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { getAssociatedTokenAddressSync, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { Config, estimateFeesPerGas, estimateGas, getAccount, getBalance, getPublicClient, readContract, readContracts, switchChain } from "@wagmi/core";
import { debounceTime, filter } from "rxjs";
import { Address, encodeFunctionData, formatEther, formatUnits, parseEther } from "viem";

import { environment } from "../../../environments/environment";
import { CHAINS, getParam } from "../../shared/constants/app-enums";
import { arbTokenContract, arbUsdcContract, arbUsdtContract, bscUsdcContract, bscUsdtContract, ethUsdcContract, ethUsdtContract, getChainData, getHelperAmount, getPresaleContract, isEmptyObject, isSolanaActivated, multicallBscContract, multicallContractArb, multicallContractPoly, multicallEthContract, polyUsdcContract, polyUsdtContract, presaleContractArb, presaleContractBsc, presaleContractEth, presaleContractPoly, skipIndexUpdate, stakeContract } from "../../shared/constants/wallet-connect.const";
import { getTokenContracts, processTokenData } from "../../shared/constants/widget-enum";
import { IBonusTier } from "../../shared/interfaces/common.interface";
import { IBalnceDataEVM, IBalnceDataSOL, ITokenomics } from "../../shared/interfaces/wallet.interface";
import { AnalyticsService } from "../analytics.service";
import { StakingService } from "../evm-services/staking.service";
import { PopupService } from "../popup.service";
import { SolanaInitialisationService } from "../sol-services/solana-initialise.service";
import { ReownService } from "./reown.service";
import { arbitrum, arbitrumSepolia, bsc, bscTestnet, mainnet, polygon, polygonAmoy, sepolia } from "viem/chains";



/**
 * Core data service managing the global presale state across multiple chains (EVM and Solana).
 * Uses Angular Signals for reactive state management and RxJS for external event streams.
 */
@Injectable({
    providedIn: 'root',
})
export class PresaleDataService {
    private refreshRate: number = 10000;
    private oldWalletAddress: string = "";
    private oldWalletAddressSOL: string = "";
    private isSolIntilised: boolean = false;
    private isSoldOut: boolean = false;
    // Serializes getPresalesData calls so parallel triggers don't double RPC traffic.
    private _inflight: Promise<void> = Promise.resolve();
    gasFeesEth = environment.eth.gasFees;
    gasFeesBsc = environment.bsc.gasFees;
    gasFeesPoly = environment.polygon.gasFees;
    gasFeesArb = environment.arbitrum.gasFees;
    gasFeesSol = environment.sol.gasFees;
    refreshIdGas!: NodeJS.Timeout;
    refreshRateGas: number = 10000;

    public refreshId!: NodeJS.Timeout;
    public wagmiConfig!: Config;
    public walletAddress!: string;
    public walletAddressSOL!: string;
    public localStep: number = 0;
    public chainId!: number; // don't change this untill it is coming from reown service
    public balanceDataEVM: IBalnceDataEVM = {} as IBalnceDataEVM;
    public balanceDataSOl: IBalnceDataSOL = {} as IBalnceDataSOL;
    /** Full presale data (balances, tokens sold, etc.) used by claim and staking views. */
    private readonly _balanceData = signal<{ sol: IBalnceDataSOL; evm: IBalnceDataEVM }>({ sol: {} as IBalnceDataSOL, evm: {} as IBalnceDataEVM });
    readonly balanceData = this._balanceData.asReadonly();

    /** Subset of balance data specifically for the buy widget to optimize change detection. */
    private readonly _widgetBalanceData = signal<{ sol: IBalnceDataSOL; evm: IBalnceDataEVM }>({ sol: {} as IBalnceDataSOL, evm: {} as IBalnceDataEVM });
    readonly widgetBalanceData = this._widgetBalanceData.asReadonly();

    /** Reactive signal for the current wallet address. */
    private readonly _walletAddressSignal = signal<string>('');
    readonly walletAddressSignal = this._walletAddressSignal.asReadonly();
    public tokenomics: ITokenomics[] = [];
    public userStaked: number = 0
    public stakeApy: number = 0
    public activeChain: string = CHAINS.EVM;
    public saleToken: Address = "" as Address;
    public connectedWalletName: string = "";
    public externalTokenContract: any[] = [];
    public isReferalAvailable: boolean = false;



    private readonly reownService = inject(ReownService);
    private readonly solService = inject(SolanaInitialisationService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly popupService = inject(PopupService);
    private readonly translateService = inject(TranslateService);
    private readonly http = inject(HttpClient);
    private readonly stakeService = inject(StakingService);

    _isBonusAvailable = signal(false);
    readonly isBonusAvailable = this._isBonusAvailable.asReadonly();

    bonusTiers: IBonusTier[] = [
        {
            "index": 0,
            "bonusPercent": 0,
            "maxAmount": 100,
            "minAmount": 0
        },
        {
            "index": 1,
            "bonusPercent": 2,
            "maxAmount": 500,
            "minAmount": 100
        },
        {
            "index": 2,
            "bonusPercent": 5,
            "maxAmount": 1000,
            "minAmount": 500
        },
        {
            "index": 3,
            "bonusPercent": 10,
            "maxAmount": 2000,
            "minAmount": 1000
        },
        {
            "index": 4,
            "bonusPercent": 20,
            "maxAmount": 5000,
            "minAmount": 2000
        },
        {
            "index": 5,
            "bonusPercent": 30,
            "maxAmount": Infinity,
            "minAmount": 5000
        }
    ];

    constructor() {
        // debounceTime(200) prevents rapid re-fetches when Reown emits multiple events on connect
        toObservable(this.reownService.connectionItems).pipe(
            debounceTime(200)
        ).subscribe((item) => {
            if (!this.wagmiConfig && item.wagmiConfig) {
                this.wagmiConfig = item.wagmiConfig;
                clearTimeout(this.refreshId);
                this.getPresalesData(this.solService.solInitialised());
            }

            if (item.activeChain) this.activeChain = item.activeChain;

            const hasNewEvmAddress = item.walletAddressEVM && item.walletAddressEVM !== this.walletAddress;
            const hasNewSolAddress = item.walletAddressSOL && item.walletAddressSOL !== this.walletAddressSOL;

            if (item.wagmiConfig && hasNewEvmAddress) {
                this.walletAddress = item.walletAddressEVM as string;
                this.externalTokenContract = getTokenContracts(this.walletAddress);
                this._walletAddressSignal.set(this.walletAddress);
                this.connectedWalletName = this.reownService.connectedWalletName;
                clearTimeout(this.refreshId);
                this.setData();
                this.getPresalesData(true);
            }

            if (!isEmptyObject(item) && hasNewSolAddress) {
                this.walletAddressSOL = item.walletAddressSOL as string;
                this._walletAddressSignal.set(this.walletAddressSOL);

                this.connectedWalletName = this.reownService.connectedWalletName;
                clearTimeout(this.refreshId);
                this.getPresalesData(true);
                this.sendSolConnectAnalytics();
            }
        });

        toObservable(this.reownService.walletDisconnected).pipe(
            filter(Boolean)
        ).subscribe(() => {
            // Reown emits 'disconnected' during cold-start even with no prior session.
            if (!this.walletAddress && !this.walletAddressSOL) {
                return;
            }
            clearTimeout(this.refreshId);
            this.walletAddress = '';
            this.walletAddressSOL = '';
            this._walletAddressSignal.set('');
            this.connectedWalletName = '';
            this.analyticsService.referUrl = '';

            // Clear user-specific fields only; presale totals are public chain state.
            const evmUserFields: (keyof IBalnceDataEVM)[] = [
                'ethClaimable', 'bscClaimable', 'polyClaimable', 'arbClaimable', 'totalClaimable',
                'nativeBalETH', 'nativeBalBSC', 'nativeBalPoly', 'nativeBalArb', 'nativeBalEthArb', 'ethBalArb',
                'usdtBalETH', 'usdtBalBSC', 'usdtBalPoly', 'usdtBalArb',
                'usdcBalETH', 'usdcBalBSC', 'usdcBalPoly', 'usdcBalArb',
            ];
            for (const k of evmUserFields) (this.balanceDataEVM as any)[k] = 0;
            const solUserFields: (keyof IBalnceDataSOL)[] = [
                'nativeBalSol', 'usdcBalSol', 'usdtBalSol', 'userClaimableSOL',
            ];
            for (const k of solUserFields) (this.balanceDataSOl as any)[k] = 0;

            this._balanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
            this._widgetBalanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });

            this.getPresalesData(this.solService.solInitialised());
        });

        toObservable(this.solService.solInitialised).subscribe((item) => {
            if (item && this.wagmiConfig?.storage) {
                clearTimeout(this.refreshId);
                this.getPresalesData(true);
            }
        });
    }


    updateCurrentNetwork(chainId: number): void {
        if (this.activeChain === CHAINS.EVM) {
            this._walletAddressSignal.set(this.walletAddress);
            this.switchNetwork(chainId)
        } else this._walletAddressSignal.set(this.walletAddressSOL);
    }

    getPresalesData(isSolInilized = false): Promise<void> {
        const next = this._inflight.then(() => this._runPresalesCycle(isSolInilized));
        this._inflight = next.catch(() => { });
        return next;
    }

    private async _runPresalesCycle(isSolInilized: boolean): Promise<void> {
        if (!this.wagmiConfig) return;

        let _solBalanceData: IBalnceDataSOL = {} as IBalnceDataSOL;

        const [, ethBalanceData, bscBalanceData, polyBalanceData, arbBalanceData, solRawData, solPriceData] = await Promise.all([
            this.prepareInitialWidgetLoadItems(isSolInilized),
            this.prepareETHContractData(),
            this.prepareBSCContractData(),
            this.preparePolyContractData(),
            this.prepareArbContractData(),
            isSolInilized ? this.getPresaleDataSOL() : Promise.resolve({} as IBalnceDataSOL),
            isSolInilized ? this.getSolprice() : Promise.resolve(null),
        ]);
        const currentStep = this.balanceDataEVM.currentStep;

        this.isReferalAvailable =
            this.chainId == environment.eth.chainIdInt ? (ethBalanceData.isReferalAvailable || false) :
                this.chainId == environment.bsc.chainIdInt ? (bscBalanceData.isReferalAvailable || false) :
                    this.chainId == environment.polygon.chainIdInt ? (polyBalanceData.isReferalAvailable || false) :
                        this.chainId == environment.arbitrum.chainIdInt ? (arbBalanceData.isReferalAvailable || false) :
                            false;

        // /* assigning the value of total token purchased by the current user */
        this.balanceDataEVM.ethClaimable = ethBalanceData.ethClaimable;
        this.balanceDataEVM.bscClaimable = bscBalanceData.bscClaimable;
        this.balanceDataEVM.polyClaimable = polyBalanceData.polyClaimable;
        this.balanceDataEVM.arbClaimable = arbBalanceData.arbClaimable;


        this.balanceDataEVM.totalClaimable = (this.balanceDataEVM.ethClaimable || 0) + (this.balanceDataEVM.bscClaimable || 0) + (this.balanceDataEVM.polyClaimable || 0) + (this.balanceDataEVM.arbClaimable || 0);
        this.balanceDataEVM = { ...this.balanceDataEVM, ...ethBalanceData, ...bscBalanceData, ...polyBalanceData, ...arbBalanceData }

        // Only overwrite balanceDataSOl when we actually fetched fresh SOL data —
        // otherwise the previous good state survives and the SOL contribution doesn't
        // briefly vanish from totalUsdRaised.
        if (isSolInilized && solPriceData && solRawData && Object.keys(solRawData).length > 0) {
            _solBalanceData = solRawData;
            const noOfTokenForOneSOl = solPriceData.Price / +this.tokenomics[currentStep]?.tokenUSDT;
            _solBalanceData.oneSol = 1 / noOfTokenForOneSOl;
            const availableToken = this.tokenomics[currentStep]?.maxToken - _solBalanceData.totalTokensSoldSOL;
            _solBalanceData.maxTokensToBuySOL = availableToken > _solBalanceData.tokenBuyLimit ? _solBalanceData.tokenBuyLimit : availableToken;
            _solBalanceData.solUsdt = +this.tokenomics[currentStep]?.tokenUSDT * Math.pow(10, 6) || 0;
            this.balanceDataSOl = _solBalanceData;
        }

        this._balanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
        this._widgetBalanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });

        if (this.reownService.sendVwoData && (this.walletAddress || this.walletAddressSOL)) {
            this.analyticsService.vwoData('walletConnectionSuccessful', {
                walletAddress: this.walletAddress || this.walletAddressSOL,
                connectionType: this.connectedWalletName,
                ethBalance: this.balanceDataEVM.nativeBalETH || 0,
                bnbBalance: this.balanceDataEVM.nativeBalBSC || 0,
                polyBalance: this.balanceDataEVM.nativeBalPoly || 0,
                arbBalance: this.balanceDataEVM.nativeBalArb || 0,
                solBalance: this.balanceDataSOl?.nativeBalSol || 0,

                stablecoinBalanceEth: (this.balanceDataEVM.usdtBalETH || 0) + (this.balanceDataEVM.usdcBalETH || 0),
                stablecoinBalanceBsc: (this.balanceDataEVM.usdtBalBSC || 0) + (this.balanceDataEVM.usdcBalBSC || 0),
                stablecoinBalancePoly: (this.balanceDataEVM.usdtBalPoly || 0) + (this.balanceDataEVM.usdcBalPoly || 0),
                stablecoinBalanceArb: (this.balanceDataEVM.usdtBalArb || 0) + (this.balanceDataEVM.usdcBalArb || 0),
                stablecoinBalanceSol: (this.balanceDataSOl?.usdtBalSol || 0) + (this.balanceDataSOl?.usdcBalSol || 0),
            });
            this.reownService.sendVwoData = false;
        }

        if (this.walletAddress && this.isSoldOut) this.getMetaData();
        if (this.refreshId) clearTimeout(this.refreshId);
        this.refreshId = setTimeout(() => this.getPresalesData(isSolInilized), this.refreshRate); // Call the self method on each 5secs interval
    }

    // usd raised, tokenssold, tokenomics preparation for initial widget loading
    async prepareInitialWidgetLoadItems(solInit = false) {
                const smartContract = {
            contracts: [

                {
                    ...presaleContractEth,
                    functionName: 'currentStep',
                    chainId: environment.eth.chainIdInt, // 0
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.eth.chainIdInt, // 1
                    args: [],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.bsc.chainIdInt, // 2
                    args: [],
                },
                {
                    ...presaleContractEth, // 3
                    functionName: 'trackRemainingTokens',
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractBsc, // 4
                    functionName: 'trackRemainingTokens',
                    chainId: environment.bsc.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, // 5 ROUND END DATES
                    args: [2],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, //6 MAX TOKENS
                    args: [0],
                },
                {
                    ...presaleContractEth,
                    functionName: 'roundDetails',
                    chainId: environment.eth.chainIdInt, //7 TOKEN USDT
                    args: [1],
                },
                {
                    ...presaleContractEth,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.eth.chainIdInt, // 8
                    args: [],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.bsc.chainIdInt, // 9
                    args: [],
                },
                {
                    ...presaleContractPoly,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.polygon.chainIdInt, // 10
                    args: [],
                },
                {
                    ...presaleContractPoly,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.polygon.chainIdInt, // 11
                    args: [],
                },
                {
                    ...presaleContractPoly, // 12
                    functionName: 'trackRemainingTokens',
                    chainId: environment.polygon.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractArb,
                    functionName: 'usdRaised', // this method is returning the total USDT raised via BSC token.
                    chainId: environment.arbitrum.chainIdInt, // 13
                    args: [],
                },
                {
                    ...presaleContractArb,
                    functionName: 'totalTokensSold', // this method is returning the no of tokens sold via BSC
                    chainId: environment.arbitrum.chainIdInt, // 14
                    args: [],
                },
                {
                    ...presaleContractArb, // 15
                    functionName: 'trackRemainingTokens',
                    chainId: environment.arbitrum.chainIdInt,
                    args: [],
                },
            ],
        } as const;

        /**
         * Above contracts are going to be read.
         */
        const _presaleData: any = await readContracts(this.wagmiConfig, smartContract);
        // const otherCryptos = processTokenData(16, _presaleData);

        // console.log('\n -------- initial _presaleData -------- \n', _presaleData)

        const currentStep = Number(_presaleData[0].result);
        if (currentStep >= this.localStep) {
            this.localStep = currentStep;
            this.balanceDataEVM.currentStep = currentStep;
        } else {
            this.balanceDataEVM.currentStep = this.localStep;
        }
        // Wagmi returns { status: 'failure', result: undefined } for a multicall slot when the
        // RPC fails. Coercing that to 0 would drop that chain's contribution from totalUsdRaised
        // and cause it to flicker on/off across refreshes — keep the last good value instead.
        const ether = (slot: any, prev: number | undefined): number =>
            slot?.status === 'success' && slot.result !== undefined ? +formatEther(slot.result) : (prev ?? 0);
        const num = (slot: any, prev: number | undefined): number =>
            slot?.status === 'success' && slot.result !== undefined ? Number(slot.result) : (prev ?? 0);

        this.balanceDataEVM.usdRaisedETH = ether(_presaleData[1], this.balanceDataEVM.usdRaisedETH);
        this.balanceDataEVM.usdRaisedBSC = ether(_presaleData[2], this.balanceDataEVM.usdRaisedBSC);
        this.balanceDataEVM.usdRaisedPoly = ether(_presaleData[10], this.balanceDataEVM.usdRaisedPoly);
        this.balanceDataEVM.usdRaisedArb = ether(_presaleData[13], this.balanceDataEVM.usdRaisedArb);
        this.balanceDataEVM.totalUsdRaised = this.balanceDataEVM.usdRaisedETH + this.balanceDataEVM.usdRaisedBSC + this.balanceDataEVM.usdRaisedPoly + this.balanceDataEVM.usdRaisedArb;
        this.balanceDataEVM.ethTokensSold = num(_presaleData[8], this.balanceDataEVM.ethTokensSold);
        this.balanceDataEVM.bscTokensSold = num(_presaleData[9], this.balanceDataEVM.bscTokensSold);
        this.balanceDataEVM.polyTokensSold = num(_presaleData[11], this.balanceDataEVM.polyTokensSold);
        this.balanceDataEVM.arbTokensSold = num(_presaleData[14], this.balanceDataEVM.arbTokensSold);
        this.balanceDataEVM.totalTokensSold = this.balanceDataEVM.ethTokensSold + this.balanceDataEVM.bscTokensSold + this.balanceDataEVM.polyTokensSold + this.balanceDataEVM.arbTokensSold;

        let solUnsoldArr: number[] = [];
        if (solInit) {
            // stateInfo is fetched only for tokensLeftUnsold here; SOL totals are written by getPresaleDataSOL.
            const stateInfo = await this.solService.program.account.stateInfo.fetch(this.solService.stateInfoAddress[0]);
            const soldArray = stateInfo?.tokensLeftUnsold?.toString()?.split(',') ?? [];
            solUnsoldArr = skipIndexUpdate(soldArray.map((val: any) => Number(val)), []);
        }

        this.tokenomics = await this.prepareTokenomics(_presaleData, currentStep, solUnsoldArr);
        this.balanceDataEVM.totalUsdRaised = (this.balanceDataEVM.usdRaisedETH || 0) + (this.balanceDataEVM.usdRaisedBSC || 0) + (this.balanceDataEVM.usdRaisedPoly || 0) + (this.balanceDataEVM.usdRaisedArb || 0);        
        this._widgetBalanceData.set({ evm: this.balanceDataEVM, sol: this.balanceDataSOl });
        if(this.externalTokenContract.length) {
            this.prepareExternalTokens()
        }
    }

      // usd raised, tokenssold, tokenomics preparation for initial widget loading
    async prepareExternalTokens() {
        
        // console.log('/n --------- externalTokenContract --------- /n', this.externalTokenContract)
        const smartContract = {
            contracts: this.externalTokenContract,
        } as const;

        /**
         * Above contracts are going to be read.
         */
        const _presaleData: any = await readContracts(this.wagmiConfig, smartContract);
        processTokenData(0, _presaleData);
    }

    async prepareETHContractData(): Promise<IBalnceDataEVM> {
        const ethBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract = {
            contracts: [
                // =========== ETH Contract ==============
                {
                    ...presaleContractEth,
                    functionName: 'ethBuyHelper', // this method is returning the amount of eth required for 1 app token.
                    chainId: environment.eth.chainIdInt,  //0
                    args: [1],
                },
                {
                    ...presaleContractEth,
                    functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
                    chainId: environment.eth.chainIdInt, //1
                    args: [1],
                },
                {
                    ...presaleContractEth, // 4 MAx token allowed to buy
                    functionName: 'maxTokensToBuy', //2
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via ETH network.
                    chainId: environment.eth.chainIdInt, //3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallEthContract,
                    functionName: 'getEthBalance', // this method is returning the eth balance of user via multicall contract.
                    chainId: environment.eth.chainIdInt, //4
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...ethUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on ETH network.
                    chainId: environment.eth.chainIdInt,  //5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractEth, // To get the token address dynamically
                    functionName: 'saleToken', //6
                    chainId: environment.eth.chainIdInt,
                    args: [],
                },
                {
                    ...ethUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on ETH network.
                    chainId: environment.eth.chainIdInt, //7
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...stakeContract,
                    functionName: 'poolStakers',
                    chainId: environment.eth.chainIdInt, // 8
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...stakeContract,
                    functionName: 'tokensStaked',
                    chainId: environment.eth.chainIdInt, // 9
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'getLatestPrice', // this function is returning the 1 eth price in usd
                    chainId: environment.eth.chainIdInt, // 10
                    args: [],
                },
                {
                    ...presaleContractEth,
                    functionName: 'blacklistedReferrers', // this method is returning the referral status on ETH network.
                    chainId: environment.eth.chainIdInt, // 11
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract);
        // console.log("---presaleDataETH", { presaleData });
        if (this.walletAddress) {
            ethBalanceData.nativeBalETH = +formatEther(presaleData[4]?.result || 0);
            ethBalanceData.usdtBalETH = +formatUnits(presaleData[5].result, 6);
            ethBalanceData.usdcBalETH = +formatUnits(presaleData[7].result, 6);
        } else {
            ethBalanceData.usdtBalETH = 0;
            ethBalanceData.nativeBalETH = 0;
            ethBalanceData.usdcBalETH = 0;
        }

        /* assigning the value of no of token sold va bsc and eth contract */
        ethBalanceData.maxTokensToBuyEVM = Number(presaleData[2].result);
        ethBalanceData.isReferalAvailable = !!Number(presaleData[11].result);
        this.saleToken = presaleData[6].result

        /* assigning the value of total USDT raised via eth bsc and poly */
        try {
            ethBalanceData.buyHelperETH = +formatEther(presaleData[0].result); // no of app tokens in exchange of 1 eth
            ethBalanceData.usdtBuyHelperETH = Number(presaleData[1].result); // no of app tokens in exchange of 1 Eth USDT
            ethBalanceData.ethPriceInUsd = +formatEther(presaleData[10].result); // no of app tokens in exchange of 1 Eth USDT
        } catch {
            // Buy-helper call fails when presale is over — reset to zero to prevent stale values
            ethBalanceData.buyHelperETH = 0;
            ethBalanceData.usdtBuyHelperETH = 0;
            ethBalanceData.ethPriceInUsd = 0;
        }

        /* assigning the value of total token purchased by the current user */
        ethBalanceData.ethClaimable = +formatEther(presaleData[3].result || 0);
        this.userStaked = +formatEther(presaleData[8].result[0] || 0);
        const totalStaked = +formatEther(presaleData[9].result || 0);
        this.stakeApy = +((+environment.apyCalcConst / totalStaked) * 100).toFixed(0);
        return Promise.resolve(ethBalanceData)
    }

    async preparePolyContractData(): Promise<IBalnceDataEVM> {
        const polyBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract = {
            contracts: [
                // =========== POLY Contract ==============
                {
                    ...presaleContractPoly,
                    functionName: 'maticBuyHelper', // this method is returning the amount of bnb required for 1 app token.
                    chainId: environment.polygon.chainIdInt,  //0
                    args: [1],
                },
                {
                    ...presaleContractPoly,
                    functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
                    chainId: environment.polygon.chainIdInt, //1
                    args: [1],
                },
                {
                    ...presaleContractPoly,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via base network.
                    chainId: environment.polygon.chainIdInt, //2
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallContractPoly,
                    functionName: 'getEthBalance', // this method is returning the base balance of user via multicall contract.
                    chainId: environment.polygon.chainIdInt, //3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...polyUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on base network.
                    chainId: environment.polygon.chainIdInt,  //4
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...polyUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on base network.
                    chainId: environment.polygon.chainIdInt, //5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractPoly,
                    functionName: 'getLatestPrice', // this function is returning the 1 eth price in usd
                    chainId: environment.polygon.chainIdInt, // 6
                    args: [],
                },
                {
                    ...presaleContractPoly,
                    functionName: 'blacklistedReferrers',
                    chainId: environment.polygon.chainIdInt, //7
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract)
        // console.log('POLYGON Presale Data:', presaleData);

        if (this.walletAddress) {
            polyBalanceData.nativeBalPoly = +formatEther(presaleData[3]?.result || 0);
            polyBalanceData.usdtBalPoly = +formatUnits(presaleData[4].result, 6);
            polyBalanceData.usdcBalPoly = +formatUnits(presaleData[5].result, 6);
            polyBalanceData.polyPriceInUsd = +formatEther(presaleData[6].result || 0);
        } else {
            polyBalanceData.usdtBalPoly = 0;
            polyBalanceData.nativeBalPoly = 0;
            polyBalanceData.usdcBalPoly = 0;
            polyBalanceData.polyPriceInUsd = 0;
        }

        /* assigning the value of total USDT raised via eth bsc and poly */
        try {
            polyBalanceData.buyHelperPoly = +formatEther(presaleData[0].result); // no of app tokens in exchange of 1 eth
            polyBalanceData.usdtBuyHelperPoly = Number(presaleData[1].result); // no of app tokens in exchange of 1 Eth USDT
        } catch (e) {
            console.log('Presale over');
            polyBalanceData.buyHelperPoly = 0; //reset value
            polyBalanceData.usdtBuyHelperPoly = 0; //reset value
        }
        polyBalanceData.isReferalAvailable = !!Number(presaleData[7].result);

        /* assigning the value of total token purchased by the current user */
        polyBalanceData.polyClaimable = +formatEther(presaleData[2].result || 0);
        // console.log("polyBalanceData", polyBalanceData);
        return Promise.resolve(polyBalanceData)
    }

    async prepareArbContractData(): Promise<IBalnceDataEVM> {
        const arbBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract: any = {
            contracts: [
                // =========== ARB Contract ==============
                {
                    ...presaleContractArb,
                    functionName: 'arbBuyHelper', // this method is returning the amount of bnb required for 1 app token.
                    chainId: environment.arbitrum.chainIdInt,  //0
                    args: [1],
                },
                {
                    ...presaleContractArb,
                    functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
                    chainId: environment.arbitrum.chainIdInt, //1
                    args: [1],
                },
                {
                    ...presaleContractArb,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via base network.
                    chainId: environment.arbitrum.chainIdInt, //2
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallContractArb,
                    functionName: 'getEthBalance', // this method is returning the base balance of user via multicall contract.
                    chainId: environment.arbitrum.chainIdInt, //3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...arbUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on base network.
                    chainId: environment.arbitrum.chainIdInt,  //4
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...arbUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on base network.
                    chainId: environment.arbitrum.chainIdInt, //5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...arbTokenContract,
                    functionName: 'balanceOf', // index 6: Testnet ARB Balance (0x8fAf...)
                    chainId: environment.arbitrum.chainIdInt, //6
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractArb,
                    functionName: 'ethBuyHelper', // this method is returning the amount of bnb required for 1 app token.
                    chainId: environment.arbitrum.chainIdInt,  //7
                    args: [1],
                },
                {
                    ...presaleContractArb,
                    functionName: 'getLatestPrice', // this function is returning the 1 eth price in usd
                    chainId: environment.arbitrum.chainIdInt, // 8
                    args: [],
                },
                {
                    ...presaleContractArb,
                    functionName: 'blacklistedReferrers',
                    chainId: environment.arbitrum.chainIdInt, //9
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract)
        // console.log('ARB Presale Data:', presaleData);

        if (this.walletAddress) {
            arbBalanceData.nativeBalEthArb = +formatEther(presaleData[3]?.result || 0);
            arbBalanceData.usdtBalArb = +formatUnits(presaleData[4].result, 6);
            arbBalanceData.usdcBalArb = +formatUnits(presaleData[5].result, 6);
            arbBalanceData.nativeBalArb = +formatEther(presaleData[6]?.result || 0);
            arbBalanceData.arbPriceInUsd = +formatEther(presaleData[8].result || 0);
        } else {
            arbBalanceData.usdtBalArb = 0;
            arbBalanceData.nativeBalArb = 0;
            arbBalanceData.usdcBalArb = 0;
            arbBalanceData.nativeBalArb = 0;
            arbBalanceData.arbPriceInUsd = 0;
        }


        /* assigning the value of total USDT raised via eth bsc and poly */
        try {
            arbBalanceData.buyHelperArb = +formatEther(presaleData[0].result); // no of app tokens in exchange of 1 eth
            arbBalanceData.usdtBuyHelperArb = Number(presaleData[1].result); // no of app tokens in exchange of 1 Eth USDT
            arbBalanceData.buyHelperARBEth = +formatEther(presaleData[7].result); // no of app tokens in exchange of 1 eth on Arbitrum
        } catch (e) {
            console.log('Presale over');
            arbBalanceData.buyHelperArb = 0; //reset value
            arbBalanceData.usdtBuyHelperArb = 0; //reset value
        }
        arbBalanceData.isReferalAvailable = !!Number(presaleData[9].result);
        /* assigning the value of total token purchased by the current user */
        arbBalanceData.arbClaimable = +formatEther(presaleData[2].result || 0);
        // console.log("arbBalanceData", arbBalanceData);
        return Promise.resolve(arbBalanceData)
    }

    async prepareBSCContractData(): Promise<IBalnceDataEVM> {
        const bscBalanceData: IBalnceDataEVM = {} as IBalnceDataEVM
        const smartContract = {
            contracts: [

                {
                    ...presaleContractBsc,
                    functionName: 'bnbBuyHelper', // this method is returning the amount of bnb required for 1 app token.
                    chainId: environment.bsc.chainIdInt, //0
                    args: [1],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'usdtBuyHelper', // this method is returning the amount of usdt required for 1 app token.
                    chainId: environment.bsc.chainIdInt, // 1
                    args: [1],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'userDeposits', // this method is returning the total token purchased by current user via BSC network.
                    chainId: environment.bsc.chainIdInt, // 2
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...multicallBscContract,
                    functionName: 'getEthBalance', // this method is returning the Bnb balance of user via multicall contract.
                    chainId: environment.bsc.chainIdInt,  //3
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...bscUsdtContract,
                    functionName: 'balanceOf', // this method is returning the usdt balance on BSC network.
                    chainId: environment.bsc.chainIdInt, //4
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...bscUsdcContract,
                    functionName: 'balanceOf', // this method is returning the usdc balance on BSC network.
                    chainId: environment.bsc.chainIdInt, // 5
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
                {
                    ...presaleContractBsc,
                    functionName: 'getLatestPrice', // this function is returning the 1 eth price in usd
                    chainId: environment.bsc.chainIdInt, // 6
                    args: [],
                },
                // Referral
                {
                    ...presaleContractBsc,
                    functionName: 'blacklistedReferrers', // this method is returning the referral status on BSC network.
                    chainId: environment.bsc.chainIdInt, // 7
                    args: [this.walletAddress || '0x0000000000000000000000000000000000000000'],
                },
            ],
        } as const;

        const presaleData: any = await readContracts(this.wagmiConfig, smartContract)
        // console.log("---prepareBSCContractData presaleData", { presaleData });


        if (this.walletAddress) {
            bscBalanceData.nativeBalBSC = +formatEther(presaleData[3]?.result || 0);
            bscBalanceData.usdtBalBSC = +formatUnits(presaleData[4].result, 18);
            bscBalanceData.usdcBalBSC = +formatUnits(presaleData[5].result, 18);
            bscBalanceData.bnbPriceInUsd = +formatEther(presaleData[6].result || 0);
        } else {
            bscBalanceData.usdtBalBSC = 0;
            bscBalanceData.nativeBalBSC = 0;
            bscBalanceData.usdcBalBSC = 0;
            bscBalanceData.bnbPriceInUsd = 0;
        }
        bscBalanceData.isReferalAvailable = !!Number(presaleData[7].result);

        /* assigning the value of no of token sold va bsc and eth contract */
        /* assigning the value of total USDT raised via eth bsc and poly */
        try {
            bscBalanceData.buyHelperBSC = +formatEther(presaleData[0].result); // no of app tokens in exchange of 1 eth
            bscBalanceData.usdtBuyHelperBSC = Number(presaleData[1].result); // no of app tokens in exchange of 1 Eth USDT
        } catch {
            // Buy-helper call fails when presale is over — reset to zero to prevent stale values
            bscBalanceData.buyHelperBSC = 0;
            bscBalanceData.usdtBuyHelperBSC = 0;
        }

        /* assigning the value of total token purchased by the current user */
        bscBalanceData.bscClaimable = +formatEther(presaleData[2].result || 0);
        return Promise.resolve(bscBalanceData)
    }

    async prepareTokenomics(presaleData: any, currentStep: number, solUnsoldArr: number[] = []): Promise<ITokenomics[]> {
        const skipIndexEth: Array<any> = [];
        const skipIndexBsc: Array<any> = [];
        const skipIndexPoly: Array<any> = [];
        const skipIndexArb: Array<any> = [];


        const ethUnsoldArr = skipIndexUpdate(presaleData[3].result || [], skipIndexEth);
        const bnbUnsoldArr = skipIndexUpdate(presaleData[4].result || [], skipIndexBsc);
        const polyUnsoldArr = skipIndexUpdate(presaleData[12].result || [], skipIndexPoly);
        const arbUnsoldArr = skipIndexUpdate(presaleData[15].result || [], skipIndexArb);
        // ethUnsoldArr.splice(<index>, 0, <new value>);
        let tokenomics: ITokenomics[] = [];
        const tokenomics2 = [];
        const _isSolanaActivated = isSolanaActivated(this.reownService.walletConnector.chainNamespaces)
        let newCounter = 0;
        for (let time of presaleData[5].result) {
            let minToken = newCounter == 0 ? 0 : Number(presaleData[6].result[newCounter - 1]);
            let maxToken = Number(presaleData[6].result[newCounter]);
            let tokenUSDT = +formatEther(presaleData[7].result[newCounter]);
            let roundInfo: ITokenomics = {
                minToken,
                maxToken,
                maxAmount: newCounter == 0 ? (maxToken - minToken) * tokenUSDT : (maxToken - minToken) * tokenUSDT + tokenomics2[newCounter - 1].maxAmount,
                tokenUSDT,
                title: 'widget.round_title',
                endDate: Number(time) * 1000,
            };
            if (newCounter < currentStep) {
                const ethVal = ethUnsoldArr[newCounter] || 0;
                const bscVal = bnbUnsoldArr[newCounter] || 0;
                const polyVal = polyUnsoldArr[newCounter] || 0;
                const arbVal = arbUnsoldArr[newCounter] || 0;
                const solVal = solUnsoldArr[newCounter] || 0;
                // if SOL chain is not enabled then do it 1 * {roundUnsoldCode}
                let roundUnSold = (_isSolanaActivated ? 4 : 3) * (roundInfo?.maxToken - roundInfo?.minToken) - (ethVal + bscVal + polyVal + arbVal + solVal);
                roundUnSold = roundUnSold * roundInfo.tokenUSDT;
                roundInfo.maxAmount = roundInfo.maxAmount + roundUnSold;
            }
            // roundInfo.maxAmount = Math.ceil(roundInfo.maxAmount)
            tokenomics2.push(roundInfo);
            newCounter++;
        }
        tokenomics = tokenomics2;
        tokenomics = [...tokenomics, tokenomics[tokenomics.length - 1]]; // cloned
        return Promise.resolve(tokenomics)
    }

    private sendSolConnectAnalytics(): void {
        if (typeof window === 'undefined') return;
        setTimeout(() => {
            if (this.walletAddressSOL && this.walletAddressSOL !== this.oldWalletAddressSOL) {
                this.oldWalletAddressSOL = this.walletAddressSOL;
                const solChainId = environment.sol.chainIdInt;
                const solTokenUSDT = this.tokenomics[this.balanceDataSOl.currentStep]?.tokenUSDT ?? 0;
                this.analyticsService.sendDashFx(this.walletAddressSOL);
                this.analyticsService.windowDataLayer('connectWallet', 'successful', 2, 1, 0, '', '', 0, 0, this.walletAddressSOL, '', this.balanceDataSOl, solChainId, solTokenUSDT);
                const subid = getParam('subid');
                this.analyticsService.postbackDataLayer('connectWallet', 0, 'null', this.balanceDataSOl, solChainId);
                this.analyticsService.postBacks('reg', subid, '', 0, this.walletAddressSOL, '');
            }
        }, 2000);
    }

    async setData(): Promise<void> {
        if (typeof window === 'undefined') return;
        // console.log(this.wagmiConfig, "--wagmiData");
        const wagmiData = getAccount(this.wagmiConfig);
        this.chainId = wagmiData.chainId as number;
        this.refreshRate = wagmiData.address ? 10000 : 20000;
        setTimeout(() => {
            if (wagmiData.address && wagmiData.address != this.oldWalletAddress) {
                this.oldWalletAddress = wagmiData.address;
                this.analyticsService.sendDashFx(this.walletAddress);
                this.analyticsService.windowDataLayer('connectWallet', 'successful', 2, 1, 0, '', '', 0, 0, this.walletAddress, "", this.balanceDataEVM, this.chainId, this.tokenomics[this.balanceDataEVM.currentStep].tokenUSDT);
                let subid = getParam('subid');
                this.analyticsService.postbackDataLayer('connectWallet', 0, 'null', this.balanceDataEVM, this.chainId);
                this.analyticsService.postBacks('reg', subid, '', 0, this.walletAddress, '');
            }
        }, 2000);
        if (environment.production) {
            setTimeout(async () => {
                this.calculateGas();
                this.calculateGas(false);
                this.gasFeesPoly = await this.calculateGasV2(environment.polygon.chainIdInt, environment.polygon.presaleAddress);
                this.gasFeesArb = await this.calculateGasV2(environment.arbitrum.chainIdInt, environment.arbitrum.presaleAddress);
                console.log("GAS FEES", { gasFeesEth: this.gasFeesEth, gasFeesBsc: this.gasFeesBsc, gasFeesPoly: this.gasFeesPoly, gasFeesArb: this.gasFeesArb });
            }, 10000)
        }
    }

    /**
  * switching the current network. If user is on wrong network
  * and got error on switching then a popup
  * will appear saying to connect correct chain
  */
    async switchNetwork(chainId: number): Promise<void> {
        console.log('switchNetwork called', this.chainId, chainId)
        if (this.chainId == chainId) return;
        else {
            const oldChainId = this.chainId;
            const newChainId = chainId;
            const newNetwork:any = environment.production ?
                (chainId === environment.eth.chainIdInt ? mainnet :
                    chainId === environment.bsc.chainIdInt ? bsc :
                        chainId === environment.polygon.chainIdInt ? polygon : arbitrum) :
                (chainId === environment.eth.chainIdInt ? sepolia :
                    chainId === environment.bsc.chainIdInt ? bscTestnet :
                        chainId === environment.polygon.chainIdInt ? polygonAmoy : arbitrumSepolia);

            this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: true, networkSwitchCompleted: false, oldChainId, newChainId });

            /* await switchChain(this.wagmiConfig, {
                 chainId: chainId,
             }).then(
                 (success: any) => {
                     // this.appRef.tick();
                     this.chainId = chainId;
                     this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: false, networkSwitchCompleted: true, oldChainId, newChainId });
                 },
                 (err: any) => {
                     const chain = chainId == environment.eth.chainIdInt ? environment.eth : environment.bsc;
                     const chainName = chain.chainInfo.params[0].chainName;
                     this.popupService.messagePopup({ icon: 'info', desc: this.translateService.instant('popup.switchNetwork', { chain_name: chainName }), confirmBtnTxt: this.translateService.instant('misc.ok') });
                 },
             );*/

            await this.reownService.walletConnector.switchNetwork(newNetwork).then((success: any) => {
                console.log(success, "switchNetwork success");
                this.chainId = chainId;
                this.analyticsService.vwoData('networkSwitch', { networkSwitchInitiated: false, networkSwitchCompleted: true, oldChainId, newChainId });
            }).catch((error: any) => {
                console.log(error, "switchNetwork error");
                const chain = chainId == environment.eth.chainIdInt ? environment.eth : environment.bsc;
                const chainName = chain.chainInfo.params[0].chainName;
                this.popupService.messagePopup({ icon: 'info', desc: this.translateService.instant('popup.switchNetwork', { chain_name: chainName }), confirmBtnTxt: this.translateService.instant('misc.ok') });
            })
        }
    }


    // SOL BALANCE AREA //

    async getPresaleDataSOL(): Promise<IBalnceDataSOL> {
        const _balanceData: IBalnceDataSOL = {} as IBalnceDataSOL;
        try {
            let _balanceDataConnected: IBalnceDataSOL = {} as IBalnceDataSOL;
            // const accountInfo = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
            const stateInfo = await this.solService.program.account.stateInfo.fetch(this.solService.stateInfoAddress[0]); // this.stateInfoAddress[0] = Public Key for State Info

            // // paymentWalletUsdtAccount - The SPL token account that receives USDT payments
            // // paymentWalletSolAccount -	A wallet address (public key) that receives SOL payments
            // this.paymentWalletUsdtAccount = accountInfo.paymentWalletUsdtAccount;
            // this.paymentWalletUsdcAccount = accountInfo.paymentWalletUsdcAccount;
            // this.paymentWalletSolAccount = accountInfo.paymentWallet;
            _balanceData.paused = stateInfo.pause;
            _balanceData.currentStep = stateInfo.currentTier;
            _balanceData.totalTokensSoldSOL = Number(stateInfo.tokensSold);
            _balanceData.usdRaised = Number(stateInfo.totalUsdRaised) / 1000000;
            // console.log(`[USD-RAISED] chain=SOL status=success value=$${_balanceData.usdRaised.toFixed(2)}`);

            _balanceData.tokenBuyLimit = Number(stateInfo.maxTokensToSell);
            const skipIndexSol: Array<any> = [];
            const soldArray = stateInfo?.tokensLeftUnsold?.toString()?.split(',')
            const solUnsoldArr = skipIndexUpdate(
                soldArray.map((val: any) => Number(val)),
                skipIndexSol,
            );
            _balanceData.unsoldArr = solUnsoldArr;

            if (this.walletAddressSOL) {
                _balanceDataConnected = await this.getMetaDataSOL(_balanceData);
            }
            // console.log("🚀 ~ file: presale.service.ts:439 ~ PresaleService ~ getPresaleDataSOL ~ _balanceDataConnected:", _balanceDataConnected)

            this.gasFeesSol = await this.estimateTotalFeeSol();
            // console.log("Estimated Gas Fees for SOL Transaction:", this.gasFeesSol);
            return Promise.resolve({ ..._balanceData, ..._balanceDataConnected })
        } catch {
            return Promise.resolve(_balanceData);
        }

    }

    /**
   * Get the USDT raised value from Lambda
   */

    getSolprice(): Promise<any> {
        return this.http.get('https://api.diadata.org/v1/assetQuotation/Solana/0x0000000000000000000000000000000000000000').toPromise();
    }

    /**
   * Get the metadata of the connected user
   */
    async getMetaDataSOL(balanceData: IBalnceDataSOL): Promise<IBalnceDataSOL> {
        // === get SOL balance ===== 1 SOL = 1,000,000,000 lamports, this.lamport_per_sol = 1_000_000_000
        balanceData.nativeBalSol = Number(await this.solService.connection.getBalance(this.solService.solWalletPubkey)) / this.solService.lamports_per_sol;

        // === get USDT balance =====

        try {
            // <---------------       USDC BALANCE         -------------------------->
            // getAssociatedTokenAddressSync -> Gets Associated Token Account Address of USDC for that specific User
            const usdcTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDC_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const usdcAccountInfo = await this.solService.connection.getTokenAccountBalance(usdcTokenAccount);
            balanceData.usdcBalSol = usdcAccountInfo.value.uiAmount || 0;
        } catch (e) {
            balanceData.usdcBalSol = 0;
        }

        try {
            // <---------------       USDT BALANCE         -------------------------->
            const usdtTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDT_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            const usdtAccountInfo = await this.solService.connection.getTokenAccountBalance(usdtTokenAccount);
            balanceData.usdtBalSol = usdtAccountInfo.value.uiAmount || 0;
        } catch (e) {
            balanceData.usdtBalSol = 0;
        }

        // this.web3PublicKey.findProgramAddressSync returns [Public Key, Size]
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([this.solService.solWalletPubkey.toBuffer()], this.solService.programId);
        let userInfo;

        try {
            userInfo = await this.solService.program.account.userInfo.fetch(userInfoAddress[0]);
        } catch (e) {
            // console.log("Presale over.", e);
            userInfo = null;
        }
        //  balanceData.userClaimable  = userInfo ? Number(userInfo.value) / LAMPORTS_PER_SOL : 0;
        balanceData.userClaimableSOL = userInfo ? Number(userInfo.value) : 0;
        return Promise.resolve(balanceData)
    }


    // Multi connection Item
    connectWallet(chain: "SOL" | "ETH" | "" = "") {
        this.reownService.connectWallet(chain)
    }

    disconnectWallet() {
        this.reownService.disconnectWallet()
    }


    async estimateTotalFeeSol(): Promise<number> {
        // --- 1. Estimate Required Compute Units (CU) via Simulation ---
        const latestBlockhash = await this.solService.connection.getLatestBlockhash();
        const accountInfo: any = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
        const walletPubkey = new this.solService.web3PublicKey(environment.solGasAddress)
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([walletPubkey.toBuffer()], this.solService.programId);
        const transactionObj = await this.solService.program.transaction.buyToken(new this.solService.BN(10), {
            accounts: {
                presaleAccount: this.solService.presaleAccountAddress[0],
                stateInfo: this.solService.stateInfoAddress[0],
                userInfo: userInfoAddress[0],
                signer: walletPubkey, //this.provider.wallet.publicKey,
                // pythPriceFeed: new this.solanaWeb3.this.web3PublicKey(environment.pythPriceFeed),
                paymentWallet: accountInfo.paymentWallet,
                systemProgram: this.solService.systemProgram.programId,
                otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
                chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
                chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
                refPubkey: accountInfo.refPubkey,
            },
        });
        transactionObj.feePayer = walletPubkey;
        transactionObj.recentBlockhash = latestBlockhash.blockhash;

        // Set a temporary fee payer for simulation if not already set
        if (!transactionObj.feePayer) {
            // Fee payer required for simulation — caller must set this before calling estimateTotalFeeSol
        }

        const simulationResult: any = await this.solService.connection.simulateTransaction(transactionObj);

        if (simulationResult.value.err) {
            throw new Error("Transaction simulation failed: " + JSON.stringify(simulationResult.value.err));
        }

        // CUs consumed from simulation + a small buffer
        const estimatedCU = Math.ceil(simulationResult?.value?.unitsConsumed * 1.3); // 30% buffer

        // --- 2. Estimate Competitive Compute Unit Price (CU Price) ---
        // Look up recent fees for the main program accounts involved for a "local fee market" estimate
        // Map the keys to their Public Key objects, not their string representation
        const accountPubKeys: any[] = transactionObj.instructions.flatMap((ix: any) =>
            ix.keys.map((k: any) => k.pubkey)
        );        // Filter out duplicates based on the string representation, but keep the object reference
        const uniqueKeysMap = new Map();
        accountPubKeys.forEach((pubkey) => {
            // Only add it if it hasn't been added yet (by its Base58 string)
            if (!uniqueKeysMap.has(pubkey.toBase58())) {
                uniqueKeysMap.set(pubkey.toBase58(), pubkey);
            }
        });

        // Convert the Map values back into an array of Public Key objects
        const uniquePubKeys = Array.from(uniqueKeysMap.values());

        // Fetch recent fees
        const feeHistory = await this.solService.connection.getRecentPrioritizationFees({ lockedWritableAccounts: uniquePubKeys });

        // Choose the median or a high percentile fee from recent successful transactions
        const paidFees = feeHistory
            .filter(fee => fee.prioritizationFee > 0)
            .map(fee => fee.prioritizationFee)
            .sort((a, b) => a - b); // Sort to find median

        let cuPriceMicroLamports = 0; // Default to zero if no one is paying priority fee
        if (paidFees.length > 0) {
            // Use the median fee as a good balance for inclusion
            const medianIndex = Math.floor(paidFees.length / 2);
            cuPriceMicroLamports = paidFees[medianIndex];
        }

        // --- 3. Calculate Final Estimated Fee ---

        // A. Calculate Prioritization Fee (in Lamports)
        // 1 Lamport = 1,000,000 Micro-Lamports
        const priorityFeeLamports = (estimatedCU * cuPriceMicroLamports) / 1_000_000;

        // B. Calculate Base Fee (in Lamports)
        // Solana base fee is currently 5000 Lamports per signature.
        // Assuming 1 signature for a simple dApp transaction (yours and the fee payer's signature is the same in most cases).
        const BASE_FEE_PER_SIGNATURE = 5000;
        const numSignatures = transactionObj.signatures.length || 1; // Use actual or default to 1
        const baseFeeLamports = numSignatures * BASE_FEE_PER_SIGNATURE;

        // C. Total Estimated Fee
        const totalFeeLamports = Math.ceil(baseFeeLamports + priorityFeeLamports);
        const feesInDecimal = totalFeeLamports / this.solService.lamports_per_sol;
        return feesInDecimal;
    }


    /**
     * Function to calculate the gas fee for transaction.
     * this function convert the USDT value to app token
     * @param amount: Amount to spend
     * @returns : converted no of app tokens
     */

    async calculateGas(isEth = true) {
        try {

            const publicClient = getPublicClient(this.wagmiConfig);
            if (!publicClient) {
                this.refreshIdGas = setTimeout(() => this.calculateGas(isEth), this.refreshRateGas);
                return;
            }
            let chainId = isEth ? environment.eth.chainIdInt : environment.bsc.chainIdInt;
            const presaleAdd = isEth ? environment.eth.presaleAddress : environment.bsc.presaleAddress;

            // Step 1: Prepare contract config
            // All buyWith* ABIs end with `address _referrer`. Pass zero address for simulation.
            const referrer = '0x0000000000000000000000000000000000000000';
            const contractConfig = {
                ...getPresaleContract(chainId),
                functionName: getChainData(chainId).nativeFunction,
                chainId: chainId,
                args: getChainData(chainId).hasStaking ? [1000, true, referrer] : [1000, referrer],
                value: parseEther(`${(this.balanceDataEVM.buyHelperETH || 1) * 1000}`),
            };

            const nativeCallData = encodeFunctionData(contractConfig);


            // Step 2: Estimate gas limit
            const estimatedGas = await estimateGas(this.wagmiConfig, {
                data: nativeCallData,
                account: environment.gasAddress as any,
                to: presaleAdd as Address,
                value: parseEther("0.1"),
            });

            // Step 3: Apply 1.3x buffer like MetaMask //Optional
            // const bufferedGas = estimatedGas;
            const bufferedGas = (estimatedGas * 15n) / 10n;

            // Step 4: Get base fee from current block
            const block = await publicClient.getBlock();
            const baseFeePerGas = block["baseFeePerGas"] ?? 0n;

            // Step 5: Get priority fee estimate
            const { maxPriorityFeePerGas = 3_000_000_000n } =
                await publicClient.estimateFeesPerGas();  // 1 Gwei minimum

            const adjustedPriority =
                maxPriorityFeePerGas < 3_000_000_000n
                    ? 3_000_000_000n
                    : maxPriorityFeePerGas;     // 1 Gwei minimum
            const maxFeePerGas = baseFeePerGas + adjustedPriority;
            // const adjustedMaxFee =  applyBuyGasBuffer(maxFeePerGas)

            // Step 6: Final gas cost in ETH
            const gasCostWei = bufferedGas * maxFeePerGas;
            const gasCostEth = formatEther(gasCostWei);

            if (isEth)
                this.gasFeesEth = +gasCostEth;
            else
                this.gasFeesBsc = +gasCostEth;
            if (this.refreshIdGas) clearTimeout(this.refreshIdGas);
            this.refreshIdGas = setTimeout(() => this.calculateGas(isEth), this.refreshRateGas);

        } catch {
            // Gas estimation failed — retry on next interval
            this.refreshIdGas = setTimeout(() => this.calculateGas(isEth), this.refreshRateGas);
        }
        // Call the self method on each 5secs interval
    }

    async calculateGasV2(chainId: number, presaleAdd: string): Promise<number> {
        try {
            const publicClient = getPublicClient(this.wagmiConfig, { chainId });
            if (!publicClient) return 0;

            const chainData = getChainData(chainId);
            const SIMULATED_TOKENS = 10n;
            const helperFunctionName =
                chainId === environment.polygon.chainIdInt ? 'maticBuyHelper'
                    : chainId === environment.bsc.chainIdInt ? 'bnbBuyHelper'
                        : 'ethBuyHelper';

            // Read exact native price so msg.value matches the contract's price check byte-for-byte.
            const exactPriceWei = await readContract(this.wagmiConfig, {
                ...getPresaleContract(chainId),
                functionName: helperFunctionName,
                chainId,
                args: [SIMULATED_TOKENS],
            }) as bigint;
            // 1% buffer absorbs a tier-boundary price shift between this read and the estimateGas call.
            const simulatedValue = (exactPriceWei * 101n) / 100n;

            // Deployed Pol/Arb presales use single-arg buyWith*(uint256) (no referrer).
            // When the referrer-enabled contract is deployed, swap to the commented form.
            // const simulatedArgs = [SIMULATED_TOKENS];
            const referrer = '0x0000000000000000000000000000000000000000';
            const simulatedArgs = chainData.hasStaking ? [SIMULATED_TOKENS, true, referrer] : [SIMULATED_TOKENS, referrer];

            const nativeCallData = encodeFunctionData({
                ...getPresaleContract(chainId),
                functionName: chainData.nativeFunction,
                args: simulatedArgs,
            });
            const simulationAccount = (environment.gasAddress || '0x0000000000000000000000000000000000000001') as Address;
            // stateOverride gives the dummy sender fake balance so the node skips its msg.value>balance pre-check.
            const estimateGasConfig: any = {
                chainId,
                data: nativeCallData,
                account: simulationAccount,
                to: presaleAdd as Address,
                value: simulatedValue,
                stateOverride: [{ address: simulationAccount, balance: parseEther('1000') }],
            };
            if ([environment.polygon.chainIdInt, environment.arbitrum.chainIdInt].includes(chainId)) {
                const feeData = await estimateFeesPerGas(this.wagmiConfig, { chainId, type: 'eip1559' });
                estimateGasConfig.maxFeePerGas = feeData.maxFeePerGas;
                estimateGasConfig.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;
            }

            const estimatedGas = await estimateGas(this.wagmiConfig, estimateGasConfig);
            const bufferedGas = (estimatedGas * 12n) / 10n;
            const gasPrice = await publicClient.getGasPrice();
            const gasCostNative = +formatEther(bufferedGas * gasPrice);
            // console.log(`[GasV2] chain=${chainId} gas=${gasCostNative}`);
            return gasCostNative;

        } catch (error) {
            console.log(`[GasV2] chain=${chainId} FAILED`, error);
            return 0;
        } finally {
            // if (this.refreshIdGas) clearTimeout(this.refreshIdGas);
            // this.refreshIdGas = setTimeout(() => this.calculateGasV2(chainId, presaleAdd), this.refreshRateGas);
            setTimeout(() => this.calculateGasV2(chainId, presaleAdd), this.refreshRateGas);
        }
    }

    gasFees(chainId: number) {
        // console.log('CHAIN ID', chainId)
        switch (chainId) {
            case environment.eth.chainIdInt:
                return this.gasFeesEth;
            case environment.bsc.chainIdInt:
                return this.gasFeesBsc;
            case environment.polygon.chainIdInt:
                return this.gasFeesPoly;
            case environment.arbitrum.chainIdInt:
                return this.gasFeesArb;
            case environment.sol.chainIdInt:
                return this.gasFeesSol;
            default:
                return this.gasFeesEth;
        }
    }

    /**
* get App token balance from the user's wallet.
* This is for further stake from wallet balance
*/

    async getMetaData() {
        const tokenBalance = await getBalance(this.wagmiConfig, {
            address: this.walletAddress as any,
            token: this.saleToken as any,
            chainId: getChainData(this.chainId).chainIdInt,
        });

        /* user's Eth/Bnb and USDT balance */
        this.stakeService.updateUserBal(+formatUnits(tokenBalance.value, tokenBalance.decimals));
    }


    async isBonusEnabled() {
        try {
            const smartContracts = {
                contracts: [
                    {
                        ...presaleContractEth,
                        functionName: 'enableBonuses',
                        chainId: environment.eth.chainIdInt,
                        args: [],
                    },
                ],
            };

            const result = await readContracts(this.wagmiConfig, smartContracts);
            this._isBonusAvailable.set(Boolean(result[0].result));
            // console.log("isBonusAvailable", this.isBonusAvailable)
            if (this.isBonusAvailable()) {
                await this.getBonusTiers();
                // console.log('TIERS', this.bonusTiers)
            }
        } catch (error) {
            this._isBonusAvailable.set(false);
        }
    };

    async getBonusTiers() {
        try {
            const smartContracts = {
                contracts: [
                    {
                        ...presaleContractEth,
                        functionName: 'bonusTiers',
                        chainId: environment.eth.chainIdInt, // 0
                        args: [0],
                    },
                    {
                        ...presaleContractEth,
                        functionName: 'bonusTiers',
                        chainId: environment.eth.chainIdInt, // 1
                        args: [1],
                    },
                    {
                        ...presaleContractEth,
                        functionName: 'bonusTiers',
                        chainId: environment.eth.chainIdInt, // 2
                        args: [2],
                    },
                    {
                        ...presaleContractEth,
                        functionName: 'bonusTiers',
                        chainId: environment.eth.chainIdInt, // 3
                        args: [3],
                    },
                    {
                        ...presaleContractEth,
                        functionName: 'bonusTiers',
                        chainId: environment.eth.chainIdInt, // 4
                        args: [4],
                    },
                    {
                        ...presaleContractEth,
                        functionName: 'bonusTiers',
                        chainId: environment.eth.chainIdInt, // 5
                        args: [5],
                    },
                ],
            };

            const result: any[] = await readContracts(this.wagmiConfig, smartContracts);
            if (result?.length) {
                this.bonusTiers = [];
                // console.log('result.length', result.length)
                for (let i = 0; i <= result.length; i++) {
                    if (result[i] && result[i]?.result) {
                        this.bonusTiers.push({
                            index: i,
                            bonusPercent: +formatUnits(result[i].result[1], 3),
                            maxAmount: +formatEther(result[i].result[0]),
                            minAmount: i ? +formatEther(result[i - 1].result[0]) : 0
                        })
                    }
                }
                this.bonusTiers[this.bonusTiers.length - 1].maxAmount = Infinity;
            }

            // console.log("TIERS", this.bonusTiers)
        } catch (error) {
            console.error(error)
        }
    };
}
